import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { Modal } from '../Modal';
import { BingoCard } from '../../progress/BingoCard';
import { UserModal } from './UserModal';
import { useStockHistory, StockLearnStatus } from '../../../hooks/useStockHistory';
import { useAuth } from '../../../hooks/useAuth';
import { usePrefersReducedMotion } from '../../../hooks/usePrefersReducedMotion';
import { useCategoryStockOptions } from '../../../hooks/useCategoryStockOptions';
import { triggerCenterConfetti } from '../../../utils/confetti';
import { normalizeStockOption } from '../../../utils/stockOptionFormatting';
import { cn } from '../../../utils/cn';
import { getCollectible, getCollectibleLogoUrl, getCollectiblesForDate, getCollectibleEventLabel, CollectibleStock } from '../../../config/collectiblesConfig';
import { getCurrentGameTimestamp } from '../../../config/dateTimeConfig';
import { getCategoryDisplayName } from '../../../config/categoryConfig';
import type { StockCategoryFilter } from '../../../types/stockCategories';

interface ProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue?: () => void;
  refreshKey?: number;
  currentSymbol?: string;
  didWin?: boolean;
  category?: StockCategoryFilter;
}

const LEGEND: { status: StockLearnStatus | 'empty'; label: string; color: string }[] = [
  { status: 'learnt', label: 'Analyzed', color: 'bg-emerald-500' },
  { status: 'guessed', label: 'Guessed', color: 'bg-amber-400' },
  { status: 'empty', label: 'Yet to guess', color: 'bg-gray-300 dark:bg-gray-600' },
];

export const ProgressModal: React.FC<ProgressModalProps> = ({ isOpen, onClose, onContinue, refreshKey, currentSymbol, didWin, category }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { stocks, isLoading, refetch } = useStockHistory(isOpen);
  const prefersReducedMotion = usePrefersReducedMotion();
  const [showUserModal, setShowUserModal] = useState(false);
  const { options } = useCategoryStockOptions(category ?? 'popular');
  const categoryTickers = useMemo(
    () => options.map((o) => normalizeStockOption(o).ticker).filter(Boolean) as string[],
    [options]
  );

  // Fire confetti when modal opens after a win
  useEffect(() => {
    if (isOpen && didWin && !prefersReducedMotion) {
      const timer = setTimeout(() => triggerCenterConfetti(2), 400);
      return () => clearTimeout(timer);
    }
  }, [isOpen, didWin, prefersReducedMotion]);

  // Refetch when refreshKey changes (e.g. after viewing Baron Wuffett analysis)
  const prevRefreshKey = useRef(refreshKey);
  useEffect(() => {
    if (refreshKey !== undefined && refreshKey !== prevRefreshKey.current) {
      prevRefreshKey.current = refreshKey;
      refetch();
    }
  }, [refreshKey, refetch]);

  // Build collectibles map from stock history + current game
  const collectiblesMap = useMemo(() => {
    const map = new Map<string, CollectibleStock>();
    for (const stock of stocks) {
      const collectible = getCollectible(stock.stockSymbol, stock.gameDate);
      if (collectible) map.set(stock.stockSymbol, collectible);
    }
    // Also check the current symbol if it just won today
    if (currentSymbol && didWin) {
      const today = getCurrentGameTimestamp();
      const collectible = getCollectible(currentSymbol, today);
      if (collectible) map.set(currentSymbol, collectible);
    }
    return map;
  }, [stocks, currentSymbol, didWin]);

  const counts = stocks.reduce(
    (acc, s) => {
      acc[s.status]++;
      return acc;
    },
    { learnt: 0, guessed: 0 } as Record<StockLearnStatus, number>
  );

  // Check if the current stock is a collectible
  const currentCollectible = currentSymbol ? collectiblesMap.get(currentSymbol) : undefined;

  // Anonymous user who won — show simplified single-stock view
  const isAnonymousWin = !user && didWin;

  // Anonymous user who lost on a collectible event day — show what they missed
  const today = getCurrentGameTimestamp();
  const eventCollectibles = getCollectiblesForDate(today);
  const eventLabel = getCollectibleEventLabel(today);
  const isAnonymousMiss = !user && !didWin && !!eventCollectibles;

  // Current category's missed collectible
  const missedCollectible = isAnonymousMiss && category ? eventCollectibles!.get(category) : undefined;

  // Other category slugs with collectibles still to play (no stock data to avoid spoilers)
  const otherCategorySlugs = useMemo(() => {
    if (!isAnonymousMiss || !eventCollectibles || !category) return [];
    return Array.from(eventCollectibles.keys()).filter((slug) => slug !== category);
  }, [isAnonymousMiss, eventCollectibles, category]);

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isAnonymousMiss ? 'Limited Edition Collectibles' : 'Progress Tracker'}
      description={isAnonymousWin || isAnonymousMiss
        ? undefined
        : 'View the Baron Wuffett analysis from results to go from "Guessed" to "Analyzed".'
      }
      animationStyle="slide-up"
      titleCentered
      className="max-w-md"
    >
      {isAnonymousWin ? (
        <div className="space-y-3">
          {/* Collectible earned announcement */}
          {currentCollectible && (
            <div className="rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-700 px-4 py-3 flex flex-col items-center gap-2 text-center">
              <img
                src={getCollectibleLogoUrl(currentCollectible)}
                alt={currentCollectible.companyName}
                className="h-10 w-10 rounded-md object-contain"
              />
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                You've earned an <span className="font-bold">April Fools Limited Edition Stamp</span>!
              </p>
            </div>
          )}

          {/* Sign-up banner */}
          <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 px-4 py-3 text-center">
            <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
              <button
                onClick={() => setShowUserModal(true)}
                className="text-blue-600 dark:text-blue-400 underline underline-offset-2 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
              >
                Create a FREE account
              </button>
              {currentCollectible
                ? ' to keep this and other rare collectibles.'
                : ' to keep track of your progress'}
            </p>
          </div>

          {/* Single stock bingo card */}
          <BingoCard stocks={[]} maxCells={15} categoryTickers={categoryTickers} currentSymbol={currentSymbol} animateCurrentSymbol currentSymbolDidWin={didWin} collectibles={collectiblesMap} />

          {/* Actions */}
          <div className="flex justify-end pt-2">
            <button
              onClick={onContinue ?? onClose}
              className="inline-flex items-center gap-1.5 rounded-lg bg-foreground text-background px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : isAnonymousMiss ? (
        <div className="space-y-4">
          {/* Missed collectible for current category */}
          {missedCollectible && (
            <div className="rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-4 py-4 flex flex-col items-center gap-2 text-center">
              <img
                src={getCollectibleLogoUrl(missedCollectible)}
                alt={missedCollectible.companyName}
                className="h-14 w-14 rounded-md object-contain grayscale opacity-40"
              />
              <p className="text-sm font-medium text-muted-foreground">
                You missed an <span className="font-bold">{eventLabel} Limited Edition</span> collectible!
              </p>
            </div>
          )}

          {/* Other categories — names only, no stock data to avoid spoilers */}
          {otherCategorySlugs.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground text-center">
                More collectibles available today in:
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {otherCategorySlugs.map((slug) => (
                  <button
                    key={slug}
                    onClick={() => { onClose(); navigate(`/${slug}`); }}
                    className="rounded-full border border-border bg-card px-3.5 py-1.5 text-xs font-semibold text-foreground hover:bg-accent transition-colors"
                  >
                    {getCategoryDisplayName(slug)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sign-up CTA */}
          <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 px-4 py-3 text-center">
            <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
              <button
                onClick={() => setShowUserModal(true)}
                className="text-blue-600 dark:text-blue-400 underline underline-offset-2 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
              >
                Create a FREE account
              </button>
              {' '}to save any collectibles you win.
            </p>
          </div>

          {/* Continue button */}
          <div className="flex justify-end pt-1">
            <button
              onClick={onContinue ?? onClose}
              className="inline-flex items-center gap-1.5 rounded-lg bg-foreground text-background px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : !user ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground">Sign in to track the stocks you've learnt.</p>
        </div>
      ) : isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-3">
          {/* Legend */}
          <div className="-mt-1 flex flex-wrap justify-center gap-3">
            {LEGEND.map(({ status, label, color }) => (
              <div key={status} className="flex items-center gap-1.5">
                <span className={cn('h-3 w-3 rounded-full', color)} />
                <span className="text-xs text-muted-foreground">{label}</span>
              </div>
            ))}
          </div>

          {/* Stats summary */}
          <div className="flex justify-center gap-4 text-sm font-medium text-foreground">
            <span>Analyzed: {counts.learnt}</span>
            <span className="text-muted-foreground">|</span>
            <span>Guessed: {counts.guessed}</span>
          </div>

          {/* Bingo grid (3 rows x 5 cols = 15 cells) */}
          <BingoCard stocks={stocks} maxCells={15} categoryTickers={categoryTickers} currentSymbol={currentSymbol} animateCurrentSymbol currentSymbolDidWin={didWin} collectibles={collectiblesMap} />

          {/* Actions */}
          <div className="flex justify-between items-center pt-2">
            <button
              onClick={() => { onClose(); navigate('/progress'); }}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Show full progress
            </button>
            <button
              onClick={onContinue ?? onClose}
              className="inline-flex items-center gap-1.5 rounded-lg bg-foreground text-background px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </Modal>

    <UserModal
      isOpen={showUserModal}
      onClose={() => setShowUserModal(false)}
    />
    </>
  );
};
